/**
 * AmenagementAttestation.jsx
 * Page publique — signature de l'attestation d'aménagement des horaires par le stagiaire
 * Modèle basé sur FO59 — PG03_EXP_FO59
 * Accessible via /amenagement-attestation/:token (sans authentification)
 */
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTraceSignature } from '../components/Shared';

const API = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;

// ── Canvas de signature (réutilisé depuis EmargementMobile) ────────────────────
function SignatureCanvas({ onSave, onCancel }) {
  const canvasRef = useRef(null);
  const trace = useTraceSignature(canvasRef, { largeurTrait: 2.5 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.getContext('2d').scale(dpr, dpr);
  }, []);

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
  };
  const save = () => {
    const canvas = canvasRef.current;
    const data = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height).data;
    if (!data.some((v, i) => i % 4 === 3 && v > 0)) { alert('Veuillez signer avant de valider.'); return; }
    onSave(canvas.toDataURL('image/png'));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 13, color: '#555', textAlign: 'center' }}>Tracez votre signature ci-dessous</div>
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: 140, border: '2px solid #1a3668', borderRadius: 8, background: '#f8faff', touchAction: 'none', cursor: 'crosshair' }}
        {...trace}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={clear}    style={btnS('#6b7280')}>Effacer</button>
        <button onClick={onCancel} style={btnS('#9ca3af')}>Annuler</button>
        <button onClick={save}     style={{ ...btnS('#1a3668'), flex: 2 }}>✓ Valider la signature</button>
      </div>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}
function today() {
  return new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function AmenagementAttestation() {
  const { token } = useParams();
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [showCanvas,setShowCanvas]= useState(false);
  const [signataire,setSignataire]= useState('');
  const [submitting,setSubmitting]= useState(false);
  const [done,      setDone]      = useState(false);

  useEffect(() => {
    fetch(`${API}/amenagement-attestation/${token}`)
      .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e.error || 'Erreur')))
      .then(d => {
        setData(d);
        // Pré-remplir le nom signataire si disponible
        const nomBD = [d.civilite, d.prenoms, d.stagiaire_nom_bd].filter(Boolean).join(' ').trim();
        if (nomBD) setSignataire(nomBD);
        else if (d.nom_stagiaire) setSignataire(d.nom_stagiaire);
      })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, [token]);

  async function handleSigner(signatureBase64) {
    if (!signataire.trim()) { alert('Veuillez indiquer votre nom complet.'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/amenagement-attestation/${token}/signer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signataire_nom: signataire.trim(), signature_base64: signatureBase64 }),
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
      setDone(true);
    } catch (e) { alert(e.message); }
    finally { setSubmitting(false); }
  }

  if (loading) return (
    <div style={pageStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <p style={{ color: '#6b7280', fontSize: 16 }}>⏳ Chargement…</p>
      </div>
    </div>
  );

  if (error) return (
    <div style={pageStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', padding: 24 }}>
        <div style={{ textAlign: 'center', background: '#fff', borderRadius: 16, padding: 32, boxShadow: '0 4px 20px rgba(0,0,0,.1)', maxWidth: 340 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
          <h2 style={{ color: '#1f2937', marginBottom: 8 }}>Lien non disponible</h2>
          <p style={{ color: '#6b7280', fontSize: 14 }}>{error}</p>
        </div>
      </div>
    </div>
  );

  if (done || data.statut_signature === 'Signé') return (
    <div style={pageStyle}>
      <div style={headerStyle}>
        <div style={{ fontSize: 11, opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>CEJ Concept — Formation</div>
        <h1 style={{ fontSize: 18, fontWeight: 800, margin: '4px 0 0' }}>Attestation signée</h1>
      </div>
      <div style={{ padding: 24, maxWidth: 480, margin: '0 auto' }}>
        <div style={{ background: '#d1fae5', border: '2px solid #6ee7b7', borderRadius: 12, padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 10 }}>✅</div>
          <h2 style={{ color: '#065f46', margin: '0 0 8px' }}>Attestation signée</h2>
          <p style={{ color: '#047857', fontSize: 14, margin: 0, lineHeight: 1.6 }}>
            Votre signature a bien été enregistrée.<br />
            {data.signataire_nom && `Signé par : ${data.signataire_nom}`}
            {data.date_signature && ` le ${fmtDate(data.date_signature)}`}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div style={pageStyle}>
      {/* En-tête */}
      <div style={headerStyle}>
        <div style={{ fontSize: 11, opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
          CEJ Concept — Formation professionnelle
        </div>
        <h1 style={{ fontSize: 18, fontWeight: 800, margin: 0, lineHeight: 1.3 }}>
          Attestation d'aménagement des horaires
        </h1>
        <p style={{ fontSize: 12, opacity: 0.75, marginTop: 4, marginBottom: 0 }}>
          Réf. : PG03_EXP_FO59
        </p>
      </div>

      <div style={{ padding: '0 16px 40px' }}>

        {/* Bloc formation */}
        <div style={cardStyle}>
          <div style={refStyle}>Organisme de formation : <strong>CEJ Concept</strong></div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, marginTop: 10 }}>
            <tbody>
              <tr>
                <td style={tdL}>Action de formation</td>
                <td style={tdV}>{data.intitule_formation || '—'}</td>
              </tr>
              <tr>
                <td style={tdL}>Date(s)</td>
                <td style={tdV}>
                  {data.date_debut
                    ? `Du ${fmtDate(data.date_debut)} au ${fmtDate(data.date_fin)}`
                    : '—'}
                </td>
              </tr>
              <tr>
                <td style={tdL}>Lieu</td>
                <td style={tdV}>{data.lieu || '—'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Bloc aménagement */}
        <div style={cardStyle}>
          <p style={{ margin: '0 0 12px', fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
            À la demande des stagiaires et avec l'accord des employeurs concernés,
            les horaires de la formation sont aménagés comme suit :
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div style={{ background: '#f3f4f6', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Horaires initiaux</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#374151' }}>{data.horaires_originaux || data.horaires || '—'}</div>
            </div>
            <div style={{ background: '#dbeafe', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 11, color: '#1d4ed8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Horaires aménagés</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1d4ed8' }}>{data.nouveaux_horaires}</div>
            </div>
          </div>

          <div style={{ background: '#fef9f0', border: '1px solid #fed7aa', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', marginBottom: 5 }}>
              Motif de l'aménagement
            </div>
            <p style={{ margin: 0, fontSize: 14, color: '#1f2937', lineHeight: 1.6 }}>{data.motif}</p>
          </div>

          <p style={{ margin: 0, fontSize: 13, color: '#6b7280', fontStyle: 'italic', lineHeight: 1.6 }}>
            La feuille d'émargement reflétera les horaires réellement réalisés.
          </p>
        </div>

        {/* Bloc signature organisme (CEJ) */}
        <div style={cardStyle}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Pour l'organisme</div>
              <div style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>CEJ Concept</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Direction</div>
              <div style={{
                height: 48, marginTop: 8, border: '1px solid #e5e7eb',
                borderRadius: 6, background: '#f9fafb',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, color: '#9ca3af', fontStyle: 'italic',
              }}>
                Signature originale
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>
                Pour le(s) stagiaire(s)
              </div>
              <div style={{ fontSize: 13, color: '#1f2937', fontWeight: 600 }}>{data.nom_stagiaire}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>{data.nom_client || '—'}</div>
            </div>
          </div>
        </div>

        {/* Bloc signature stagiaire */}
        {data.statut_entreprise !== 'Refusé' ? (
          <div style={cardStyle}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1f2937', margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              ✍ Votre signature
            </h3>
            <p style={{ fontSize: 13, color: '#374151', marginBottom: 14, lineHeight: 1.6 }}>
              En signant cette attestation, vous confirmez avoir pris connaissance de cet
              aménagement des horaires de formation et l'acceptez.
            </p>

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Nom complet *
            </label>
            <input
              type="text"
              value={signataire}
              onChange={e => setSignataire(e.target.value)}
              placeholder="Prénom NOM"
              style={inputStyle}
            />

            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                Fait le {today()}
              </div>
              {!showCanvas ? (
                <button
                  onClick={() => {
                    if (!signataire.trim()) { alert('Indiquez votre nom avant de signer.'); return; }
                    setShowCanvas(true);
                  }}
                  style={{ ...btnPrimary, width: '100%' }}>
                  ✍ Signer l'attestation
                </button>
              ) : (
                <SignatureCanvas
                  onSave={handleSigner}
                  onCancel={() => setShowCanvas(false)}
                />
              )}
            </div>
          </div>
        ) : (
          <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 10, padding: 16, textAlign: 'center', fontSize: 14, color: '#991b1b', fontWeight: 600 }}>
            ✗ Cet aménagement a été refusé par l'employeur.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const pageStyle   = { minHeight: '100vh', background: '#f3f4f6', fontFamily: "'Segoe UI', system-ui, sans-serif" };
const headerStyle = { background: 'linear-gradient(135deg, #1a3668, #2563eb)', color: '#fff', padding: '20px 20px 18px' };
const cardStyle   = { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,.08)', marginBottom: 14, marginTop: 14 };
const refStyle    = { fontSize: 12, color: '#6b7280', fontStyle: 'italic' };
const tdL         = { padding: '6px 8px 6px 0', fontSize: 12, color: '#6b7280', fontWeight: 600, width: '38%', verticalAlign: 'top', borderBottom: '1px solid #f3f4f6' };
const tdV         = { padding: '6px 0', fontSize: 13, color: '#1f2937', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' };
const inputStyle  = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' };
const btnPrimary  = { background: '#1a3668', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 16px', fontSize: 15, fontWeight: 700, cursor: 'pointer' };
const btnS = (bg) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', flex: 1 });
